import { UploadSource } from "@/components/upload/types/upload-item";
import KeyedItem from "../base/item";
import { WorkspaceDocumentMetadata } from "../metadata/metadata";
import { User } from "../users/User";

type Workspace = KeyedItem & {
  owner?: User;
  name: string;
  description: string;
  userId: string;
  documents: WorkspaceDocument[];
  folders: WorkspaceFolder[];
  /** Web links kept alongside documents and folders (LAR-415). Absent in payloads from before links existed. */
  links?: WorkspaceLink[];
  sharedAccess: Record<string, WorkspaceRole>;
  lastDocumentUpdatedAt: Date;
  sharedWithWholeTenant: boolean;
  sharedWorkspaceRole?: WorkspaceRole;
  sharedWithSubTenants?: string[];
  isParentWorkspace?: boolean;
};

export type WorkspaceFolder = KeyedItem & {
  name: string;
  parentName: string;
  documents: WorkspaceDocument[];
  folders: WorkspaceFolder[];
  /** Web links inside this folder (LAR-415). Absent in payloads from before links existed. */
  links?: WorkspaceLink[];
  createdAt: string;
  updatedAt: string;
  userId: string;
  folderSource: UploadSource;
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
};

/**
 * A saved web address shown in the files view on equal footing with documents
 * and folders. Lives at the workspace root or inside one folder.
 */
export type WorkspaceLink = KeyedItem & {
  name: string;
  url: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
};

/** What the API needs to add or edit a link: a trimmed name and an absolute http(s) address. */
export type WorkspaceLinkRequest = {
  name: string;
  url: string;
};

export type WorkspaceDocument = KeyedItem & {
  name: string;
  size: number;
  userId: string;
  documentType: WorkspaceDocumentType;
  blobName: string;
  documentSource: UploadSource;
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
  workspaceId?: string;
  subscribers?: string[];
  revisions: WorkspaceDocumentRevision[];
  activeRevision: number;
  documentMetadataList?: WorkspaceDocumentMetadata[];
};

export type SelectedWorkspaceVersion = {
  blobName: string;
  name: string;
  updatedAt: Date;
  revisionVersion: number;
  size: number;
  documentType: WorkspaceDocumentType;
};

export type WorkspaceDocumentRevision = {
  blobId: string;
  size: number;
  updatedAt: Date;
  revisionVersion: number;
  isIndexed: boolean;
  /** Set when indexed; null/legacy means search index may need reindex after pipeline upgrade. */
  indexedPipelineVersion?: number | null;
  /** True when indexed pipeline version differs from platform current (migration drift). */
  searchIndexStale?: boolean;
};

export enum WorkspaceDocumentType {
  DOCX = "DOCX",
  DOC = "DOC",
  XLS = "XLS",
  XLSX = "XLSX",
  XLSM = "XLSM",
  PDF = "PDF",
  PPT = "PPT",
  PPTX = "PPTX",
  TXT = "TXT",
  MD = "MD",
  OTHER = "OTHER",
}

export enum WorkspaceRole {
  OWNER = "Owner",
  READ = "Read",
  WRITE = "Write",
  ADMIN = "Admin",
}

// Feedback is an object with key-value pairs
// It is used to provide additional information about the operation
// typycally used for excel uploads to provide information about the sheets
// key will typycally be the sheet name and value a string message indicating changes the user can make to the sheet to make it compatible
export type AddOrUpdateWorkspaceDocumentResponseType = {
  workspace: Workspace;
  feedback: Record<string, string>;
};

export default Workspace;

export function getAllWorkspaceDocuments(
  workspace: Workspace
): WorkspaceDocument[] {
  const collectDocuments = (
    folders: WorkspaceFolder[]
  ): WorkspaceDocument[] => {
    return folders.flatMap((folder) => [
      ...folder.documents,
      ...collectDocuments(folder.folders),
    ]);
  };

  return [...workspace.documents, ...collectDocuments(workspace.folders)];
}
